import { useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { styled } from '@mui/material/styles';
import { ListingsIcon } from 'assets/images/icons';
import { MessagingIcon } from 'assets/images/sidebar';
import { useAccountPeople, usePermissions, useTranslation, useChannels } from '@hooks';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import { contactsActions } from '@actions';
import { RoutePath } from '@constants/routes';

const StyledMessagingIcon = styled(MessagingIcon)({
    width: '1rem',
    height: '1rem',
    marginRight: '8px',
    '& path': {
        fill: 'var(--primary-main)',
        fillOpacity: 1,
    },
});

const Container = styled('div', {
    shouldForwardProp: prop => prop !== 'hasResponse',
})(({ hasResponse }) => ({
    display: 'flex',
    width: '100%',
    justifyContent: hasResponse ? 'space-between' : 'flex-end',
    flexWrap: 'wrap',
    '@media (max-width: 600px)': {
        flexDirection: 'column',
    },
}));

const TicketInfoWrapper = styled('div')({
    display: 'flex',
    gap: '16px',
    '@media (max-width: 600px)': {
        gap: '0',
        flexDirection: 'column',
    },
});

const TicketInfo = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    '@media (max-width: 1200px)': {
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
    },
});

const TicketLabel = styled('div')({
    marginRight: '16px',
    maxWidth: '172px',
    minWidth: '75px',
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 1200px)': {
        maxWidth: '150px',
        minWidth: '100px',
        marginBottom: '16px',
        '&:first-of-type': {
            flex: 1,
        },
    },
});

const FeedbackToolbar = ({ data, onClickTicket }) => {
    const { t } = useTranslation();
    const { getPersonName } = useAccountPeople();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { channels, getAccessibleChannelIds, canInitiateConversation } = useChannels();
    const {
        id,
        ticket = null,
        conversationStatus,
        conversationAssigneeId,
        conversationAssigneeName,
        contactId,
        phoneNumber,
        email,
        locationId,
        conversationId,
    } = data;

    const hasResponse = Boolean(ticket) || Boolean(conversationStatus !== null);

    const handleClickTicket = useCallback(
        type => onClickTicket({ id, ticket, type, locationId }),
        [id, ticket, locationId],
    );

    const handleClickConversation = () =>
        conversationId
            ? navigate(`${RoutePath.MessagingInbox}?conversationId=${conversationId}`)
            : dispatch(
                  contactsActions.setContactInitConversationModal({
                      open: true,
                      locations: [locationId],
                      contactId,
                      ctx: { ...data, externalId: data?.contactExternalId, surveyResponseId: data?.id },
                  }),
              );

    const ticketStatusMap = {
        0: t('FeedbackToolbar.empty'),
        1: t('common.actionRequired'),
        2: t('common.inProgress'),
        3: t('FeedbackToolbar.completed'),
    };
    const conversationStatusMap = {
        0: t('InboxPage.open'),
        1: t('InboxPage.resolved'),
    };

    const availableChannels = useMemo(() => {
        return getAccessibleChannelIds(canInitiateConversation);
    }, [data, channels]);

    const { hasAccess } = usePermissions();
    const isConversationButtonAvailable =
        hasAccess('messaging') &&
        ((availableChannels.length > 0 && contactId && (phoneNumber || email)) || conversationId);

    return (
        <Container hasResponse={hasResponse}>
            <TicketInfoWrapper>
                {conversationId && (
                    <TicketInfo>
                        <TicketLabel>
                            <Typography
                                variant="caption"
                                sx={{ display: 'inline-block', marginBottom: '2px', color: 'var(--text-secondary)' }}
                            >
                                {t('common.assignedTo')}:
                            </Typography>
                            <Typography variant="body2" style={{ color: 'var(--text-primary)' }}>
                                {getPersonName(conversationAssigneeId) || conversationAssigneeName}
                            </Typography>
                        </TicketLabel>
                        <TicketLabel>
                            <Typography
                                variant="caption"
                                sx={{ display: 'inline-block', marginBottom: '2px', color: 'var(--text-secondary)' }}
                            >
                                {t('common.status')}:
                            </Typography>
                            <Typography variant="body2" style={{ color: 'var(--text-primary)' }}>
                                {conversationStatusMap[conversationStatus]}
                            </Typography>
                        </TicketLabel>
                    </TicketInfo>
                )}
                {ticket && (
                    <TicketInfo>
                        <TicketLabel>
                            <Typography
                                variant="caption"
                                sx={{ display: 'inline-block', marginBottom: '2px', color: 'var(--text-secondary)' }}
                            >
                                {t('FeedbackToolbar.ticketAssignedTo')}
                            </Typography>
                            <Typography variant="body2" style={{ color: 'var(--text-primary)' }}>
                                {getPersonName(ticket.assigneeId)}
                            </Typography>
                        </TicketLabel>
                        <TicketLabel>
                            <Typography
                                variant="caption"
                                sx={{ display: 'inline-block', marginBottom: '2px', color: 'var(--text-secondary)' }}
                            >
                                {t('FeedbackToolbar.ticketStatus')}
                            </Typography>
                            <Typography variant="body2" style={{ color: 'var(--text-primary)' }}>
                                {ticketStatusMap[ticket?.ticketStatus]}
                            </Typography>
                        </TicketLabel>
                    </TicketInfo>
                )}
            </TicketInfoWrapper>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {isConversationButtonAvailable && (
                    <Button variant="outlined" onClick={handleClickConversation}>
                        <StyledMessagingIcon />
                        {conversationId
                            ? t('InitConversation.viewConversation')
                            : t('InitConversation.newConversation')}
                    </Button>
                )}
                <Button
                    variant="outlined"
                    onClick={() => (ticket ? handleClickTicket('view') : handleClickTicket('create'))}
                >
                    <ListingsIcon style={{ width: '1rem', height: '1rem', marginRight: '8px' }} />
                    {ticket ? t('buttons.viewTicket') : t('buttons.newTicket')}
                </Button>
            </div>
        </Container>
    );
};

export default FeedbackToolbar;
